/**
 * Connection Status Indicator Component
 *
 * Displays real-time connection status and service availability
 * with automatic synchronization between frontend and backend states
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Alert,
  Button,
} from '@mui/material';
import {
  CheckCircle as ConnectedIcon,
  Error as DisconnectedIcon,
  Refresh as RefreshIcon,
  NetworkCheck as NetworkIcon,
  Cloud as ServerIcon,
  Message as WebSocketIcon,
  Schedule as PendingIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandIcon,
} from '@mui/icons-material';
import connectionTimeoutService from '../../services/connectionTimeoutService.js';
import MCP_API from '../../api/mcp.api.js';

function ConnectionStatusIndicator({ compact = false, showDetails = false }) {
  const [connectionStates, setConnectionStates] = useState({});
  const [metrics, setMetrics] = useState({});
  const [availability, setAvailability] = useState({});
  const [isChecking, setIsChecking] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Subscribe to connection state changes
  useEffect(() => {
    const handleStateChange = (data) => {
      setConnectionStates((prev) => ({
        ...prev,
        [data.service]: { state: data.state, timestamp: Date.now(), data: data.data },
      }));
      setLastUpdate(Date.now());
    };

    const handleNetworkRecovered = (data) => {
      setAvailability(data);
      setLastUpdate(Date.now());
    };

    const handleStateSynced = (data) => {
      console.log('🔄 Connection states synchronized:', data);
      setLastUpdate(Date.now());
    };

    connectionTimeoutService.on('state_change', handleStateChange);
    connectionTimeoutService.on('network_recovered', handleNetworkRecovered);
    connectionTimeoutService.on('state_synced', handleStateSynced);

    // Initial state load
    setConnectionStates(connectionTimeoutService.getConnectionStates());
    setMetrics(connectionTimeoutService.getMetrics());

    return () => {
      connectionTimeoutService.off('state_change', handleStateChange);
      connectionTimeoutService.off('network_recovered', handleNetworkRecovered);
      connectionTimeoutService.off('state_synced', handleStateSynced);
    };
  }, []);

  // Periodic metrics update (reduced frequency to avoid excessive requests)
  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics(connectionTimeoutService.getMetrics());
    }, 10000); // Update every 10 seconds instead of 2

    return () => clearInterval(interval);
  }, []);

  const checkAvailability = useCallback(async () => {
    setIsChecking(true);
    try {
      const results = await connectionTimeoutService.checkServiceAvailability('all', 5000);
      setAvailability(results);
      setLastUpdate(Date.now());
    } catch (error) {
      console.error('Failed to check availability:', error);
    } finally {
      setIsChecking(false);
    }
  }, []);

  const getStatusIcon = (state) => {
    switch (state) {
      case 'connected':
      case 'available':
      case 'online':
        return <ConnectedIcon sx={{ fontSize: 16, color: 'success.main' }} />;
      case 'disconnected':
      case 'unavailable':
      case 'offline':
        return <DisconnectedIcon sx={{ fontSize: 16, color: 'error.main' }} />;
      case 'connecting':
      case 'reconnecting':
        return <PendingIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
      default:
        return <WarningIcon sx={{ fontSize: 16, color: 'info.main' }} />;
    }
  };

  const getStatusColor = (state) => {
    switch (state) {
      case 'connected':
      case 'available':
      case 'online':
        return 'success';
      case 'disconnected':
      case 'unavailable':
      case 'offline':
        return 'error';
      case 'connecting':
      case 'reconnecting':
        return 'warning';
      default:
        return 'info';
    }
  };

  const getOverallStatus = () => {
    const states = Object.values(connectionStates);
    const availableStates = states.filter((s) => ['connected', 'available', 'online'].includes(s?.state || ''));

    if (availableStates.length === states.length && states.length > 0) {
      return 'healthy';
    } if (availableStates.length > 0) {
      return 'degraded';
    }
    return 'unhealthy';
  };

  const overallStatus = getOverallStatus();

  if (compact) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Badge
          color={getStatusColor(overallStatus)}
          variant="dot"
          overlap="circular"
        >
          <NetworkIcon sx={{ fontSize: 20 }} />
        </Badge>
        <Typography variant="body2" color="text.secondary">
          {overallStatus === 'healthy' ? 'Connected'
            : overallStatus === 'degraded' ? 'Partial' : 'Disconnected'}
        </Typography>
        <Tooltip title="Check connection status">
          <IconButton size="small" onClick={checkAvailability} disabled={isChecking}>
            <RefreshIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Card variant="outlined" sx={{ minWidth: 300 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="div">
            Connection Status
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              icon={getStatusIcon(overallStatus)}
              label={overallStatus === 'healthy' ? 'Healthy'
                : overallStatus === 'degraded' ? 'Degraded' : 'Unhealthy'}
              color={getStatusColor(overallStatus)}
              size="small"
            />
            <Tooltip title="Check connection status">
              <IconButton size="small" onClick={checkAvailability} disabled={isChecking}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Service Status Grid */}
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <Box textAlign="center">
              <WebSocketIcon sx={{ fontSize: 24, mb: 0.5, color: 'primary.main' }} />
              <Typography variant="caption" display="block">WebSocket</Typography>
              <Chip
                icon={getStatusIcon(connectionStates.websocket?.state)}
                label={connectionStates.websocket?.state || 'unknown'}
                color={getStatusColor(connectionStates.websocket?.state)}
                size="small"
                sx={{ fontSize: '0.7rem' }}
              />
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Box textAlign="center">
              <ServerIcon sx={{ fontSize: 24, mb: 0.5, color: 'secondary.main' }} />
              <Typography variant="caption" display="block">Backend</Typography>
              <Chip
                icon={getStatusIcon(connectionStates.backend?.state || (availability.backend?.available ? 'available' : 'unavailable'))}
                label={connectionStates.backend?.state || (availability.backend?.available ? 'online' : 'offline')}
                color={getStatusColor(connectionStates.backend?.state || (availability.backend?.available ? 'available' : 'unavailable'))}
                size="small"
                sx={{ fontSize: '0.7rem' }}
              />
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Box textAlign="center">
              <NetworkIcon sx={{ fontSize: 24, mb: 0.5, color: 'info.main' }} />
              <Typography variant="caption" display="block">Network</Typography>
              <Chip
                icon={getStatusIcon(connectionStates.network?.state)}
                label={connectionStates.network?.state || 'unknown'}
                color={getStatusColor(connectionStates.network?.state)}
                size="small"
                sx={{ fontSize: '0.7rem' }}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Connection Metrics */}
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Metrics (Last
            {' '}
            {lastUpdate ? Math.floor((Date.now() - lastUpdate) / 1000) : 0}
            s ago)
          </Typography>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="caption">Requests:</Typography>
            <Typography variant="caption">
              {metrics.successCount || 0}
              {' '}
              success,
              {metrics.retryCount || 0}
              {' '}
              retry,
              {metrics.requestTimeouts || 0}
              {' '}
              timeout
            </Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption">Active:</Typography>
            <Typography variant="caption">
              {metrics.activeRequests || 0}
              {' '}
              requests,
              {metrics.activeTimeouts || 0}
              {' '}
              timeouts
            </Typography>
          </Box>
        </Box>

        {/* Last Sync Time */}
        {lastUpdate && (
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary">
              Last sync:
              {' '}
              {new Date(lastUpdate).toLocaleTimeString()}
            </Typography>
          </Box>
        )}

        {/* Loading indicator */}
        {isChecking && (
          <Box mt={2}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={0.5}>
              Checking connection status...
            </Typography>
          </Box>
        )}

        {/* Detailed Information */}
        {showDetails && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandIcon />}>
              <Typography variant="subtitle2">Detailed Status</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {/* Connection States */}
                <Typography variant="body2" gutterBottom>Connection States:</Typography>
                {Object.entries(connectionStates).map(([service, state]) => (
                  <Box key={service} display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption">
                      {service}
                      :
                    </Typography>
                    <Chip
                      icon={getStatusIcon(state.state)}
                      label={state.state}
                      color={getStatusColor(state.state)}
                      size="small"
                      sx={{ fontSize: '0.6rem' }}
                    />
                  </Box>
                ))}

                {/* Service Availability */}
                <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>Service Availability:</Typography>
                {Object.entries(availability).map(([service, status]) => (
                  <Box key={service} display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption">
                      {service}
                      :
                    </Typography>
                    <Box>
                      <Chip
                        icon={getStatusIcon(status.available ? 'available' : 'unavailable')}
                        label={status.available ? 'available' : 'unavailable'}
                        color={getStatusColor(status.available ? 'available' : 'unavailable')}
                        size="small"
                        sx={{ fontSize: '0.6rem', mr: 1 }}
                      />
                      {status.latency && (
                        <Typography variant="caption" color="text.secondary">
                          {status.latency}
                          ms
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}

                {/* Connection Metrics Details */}
                <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>Connection Metrics:</Typography>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption">WebSocket Health:</Typography>
                  <Typography variant="caption">
                    {metrics.connectionStates?.websocket?.state === 'connected' ? 'healthy' : 'unhealthy'}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption">Network State:</Typography>
                  <Typography variant="caption">
                    {connectionStates.network?.state || 'unknown'}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="caption">Last Sync:</Typography>
                  <Typography variant="caption">
                    {lastUpdate ? new Date(lastUpdate).toLocaleString() : 'never'}
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Alerts */}
        {overallStatus === 'unhealthy' && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Connection issues detected. Some features may not work properly.
            </Typography>
          </Alert>
        )}

        {overallStatus === 'degraded' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Partial connectivity. Some services may be slow or unavailable.
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default ConnectionStatusIndicator;
